/** returnTo plumbing for the sign-in flow.
 *
 * The login page accepts `?returnTo=<app-relative URL>` so a bounced deep link
 * (e.g. /delivery-logs?q=…) survives authentication — including full page
 * loads, where router state is lost. `sanitizeReturnTo` is the open-redirect
 * guard: only app-relative paths pass; anything host-relative or absolute is
 * dropped.
 */
import type { Location } from 'react-router-dom';

/** Accept only app-relative paths ("/x…", not "//host", no scheme, no "\"). */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  if (raw.includes('\\')) return null;
  return raw;
}

/** The current location as a returnTo value (path + filter + hash). */
export function locationReturnTo(location: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/** The MCP sign-in handoff route (`?req=…`, backend metaplan A7c; the path is fixed by the
 *  authorization-server contract). It sits under /auth/ but needs a session, so a sign-in
 *  started from it — including a forced MFA enrolment — must come back to it. */
export const OAUTH_HANDOFF_PATH = '/auth/oauth-handoff';

/** `path` carrying `dest` as `?returnTo=`; just `path` for the home page, the default anyway. */
export function withReturnTo(path: string, dest: string): string {
  return dest === '/' ? path : `${path}?returnTo=${encodeURIComponent(dest)}`;
}

/** Where a lost session sends the user: /auth/login, carrying the interrupted location — but
 *  not from the sign-in pages themselves (nor from the home page), except the MCP handoff. */
export function loginPathFrom(location: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  const signInPage = location.pathname.startsWith('/auth/') && location.pathname !== OAUTH_HANDOFF_PATH;
  return signInPage ? '/auth/login' : withReturnTo('/auth/login', locationReturnTo(location));
}

/** Router state shape shared by LoginPage → MFAChallengePage. */
export interface AuthRedirectState {
  returnTo?: string | null;
  from?: Pick<Location, 'pathname' | 'search' | 'hash'> | null;
}

/** Post-login destination: sanitized `?returnTo=` wins, then the router-state
 *  `from` location (path + search + hash — search carries grid filters), then
 *  the dashboard root. */
export function resolveLoginDestination(
  searchParams: URLSearchParams,
  state: AuthRedirectState | null | undefined,
): string {
  const fromQuery = sanitizeReturnTo(searchParams.get('returnTo'));
  if (fromQuery) return fromQuery;
  const fromState = sanitizeReturnTo(state?.returnTo);
  if (fromState) return fromState;
  if (state?.from?.pathname) {
    return sanitizeReturnTo(locationReturnTo(state.from)) ?? '/';
  }
  return '/';
}
